/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, EventEmitter, Input, NgZone, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {PopoverDirective} from 'ngx-bootstrap/popover';
import {PopoverModule} from 'ngx-bootstrap/popover';
import {APP_SHARED} from '../app-shared';

@Component({
  standalone: true,
  imports: [...APP_SHARED, PopoverModule],
  selector: 'adn-top-n-selector',
  template: `
    <button #topNPopover="bs-popover"
            style="margin-right: 3px;"
            type="button"
            class="btn btn-default btn-xs pull-right"
            triggers="click"
            [outsideClick]="true"
            placement="bottom"
            [adaptivePosition]="true"
            boundariesElement="viewport"
            container="body"
            adnClampPopoverViewport
            [popover]="popTemplate">
      Top {{ displayTopN }}&nbsp;<span class="fa fa-caret-down"></span>
    </button>
    <ng-template #popTemplate>
      <div class="columnOptions">
        <ul>
          <li *ngFor="let level of levels">
            <button *ngIf="!isSelected(level)"
                    type="button"
                    class="btn btn-link btn-xs"
                    style="padding: 0; border: 0;"
                    (click)="updateLevel(level, $event)">Top {{ level }}</button>
            <strong *ngIf="isSelected(level)">Top {{ level }}</strong>
          </li>
        </ul>
      </div>
    </ng-template>
  `
})
export class TopNSelectorComponent implements OnChanges {
  @Input() topN: number;
  @Input() totalCount: number;
  @Output() topNChange = new EventEmitter<number>();

  @ViewChild('topNPopover', {read: PopoverDirective}) topNPopover: PopoverDirective;

  displayTopN = 50;

  private readonly defaultLevels = [50, 100, 200, 500, 1000, 2000, 5000];

  constructor(private readonly ngZone: NgZone) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.topN) {
      this.syncTopN();
    }
  }

  get levels(): number[] {
    const totalCount = Number(this.totalCount) || 0;
    if (totalCount <= 0) {
      return [];
    }

    let matchIndex = 0;
    for (let i = 0; i < this.defaultLevels.length; i++) {
      if (this.defaultLevels[i] < totalCount) {
        matchIndex = i;
      }
    }

    return this.defaultLevels.slice(0, matchIndex + 2);
  }

  isSelected(level: number): boolean {
    return this.displayTopN === level;
  }

  updateLevel(level: number, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.displayTopN = level;
    this.topNPopover?.hide();
    this.ngZone.run(() => {
      this.topNChange.emit(level);
    });
  }

  private syncTopN(): void {
    const value = Number(this.topN);
    if (!Number.isNaN(value) && value > 0) {
      this.displayTopN = value;
    }
  }
}
